/**
 * Quick-add slide-out drawer for adding income entries, assets, and liabilities.
 * Triggered by a floating action button.
 */

import { useState } from 'react';
import type { QuickAddEntry, MonthAnnotation } from '../../types/cashflow';

interface Props {
  currentMonth: string;
  onAddEntry: (entry: QuickAddEntry) => void;
  onAddAnnotation: (annotation: MonthAnnotation) => void;
}

type Tab = 'income' | 'asset' | 'liability' | 'note';

export default function QuickAddDrawer({ currentMonth, onAddEntry, onAddAnnotation }: Props) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<Tab>('income');
  const [success, setSuccess] = useState('');

  function showSuccess(msg: string) {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 2500);
  }

  return (
    <>
      {/* Floating action button */}
      <button
        onClick={() => setOpen(true)}
        title="Quick add"
        style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: 'var(--color-accent)',
          color: '#fff',
          border: 'none',
          fontSize: '1.5rem',
          cursor: 'pointer',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 100,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        +
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 }}
        />
      )}

      {/* Drawer */}
      <div
        style={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: 380,
          background: '#fff',
          boxShadow: 'var(--shadow-lg)',
          zIndex: 300,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div style={{ padding: 'var(--spacing-lg)', borderBottom: '1px solid var(--color-border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Quick Add</h3>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer', color: 'var(--color-text-secondary)' }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--color-border)' }}>
          {(['income', 'asset', 'liability', 'note'] as Tab[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              style={{
                flex: 1,
                padding: '10px 4px',
                background: 'none',
                border: 'none',
                borderBottom: tab === t ? '2px solid var(--color-accent)' : '2px solid transparent',
                cursor: 'pointer',
                fontSize: '0.8rem',
                fontWeight: tab === t ? 700 : 400,
                color: tab === t ? 'var(--color-accent)' : 'var(--color-text-secondary)',
                textTransform: 'capitalize',
              }}
            >
              {t}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--spacing-lg)' }}>
          {success && (
            <div style={{ background: '#c6f6d5', borderRadius: 'var(--radius-md)', padding: '10px 14px', marginBottom: 'var(--spacing-md)', color: '#22543d', fontWeight: 600 }}>
              ✓ {success}
            </div>
          )}

          {tab === 'income' && (
            <IncomeForm currentMonth={currentMonth} onSave={(e) => { onAddEntry(e); showSuccess('Income entry added'); }} />
          )}
          {tab === 'asset' && (
            <AssetForm currentMonth={currentMonth} onSave={(e) => { onAddEntry(e); showSuccess('Asset added'); }} />
          )}
          {tab === 'liability' && (
            <LiabilityForm currentMonth={currentMonth} onSave={(e) => { onAddEntry(e); showSuccess('Liability added'); }} />
          )}
          {tab === 'note' && (
            <NoteForm currentMonth={currentMonth} onSave={(a) => { onAddAnnotation(a); showSuccess('Note saved'); }} />
          )}
        </div>
      </div>
    </>
  );
}

function IncomeForm({ currentMonth, onSave }: { currentMonth: string; onSave: (e: QuickAddEntry) => void }) {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name || !amount) return;
    onSave({ id: crypto.randomUUID(), type: 'income', name, amount: parseFloat(amount), date: currentMonth, note });
    setName(''); setAmount(''); setNote('');
  }

  return (
    <form onSubmit={submit}>
      <Field label="Description" value={name} onChange={setName} placeholder="e.g. Consulting invoice" />
      <Field label="Amount (£)" type="number" value={amount} onChange={setAmount} placeholder="0.00" />
      <Field label="Note (optional)" value={note} onChange={setNote} placeholder="Context..." />
      <SaveButton />
    </form>
  );
}

function AssetForm({ currentMonth, onSave }: { currentMonth: string; onSave: (e: QuickAddEntry) => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('other');
  const [amount, setAmount] = useState('');
  const [cashflow, setCashflow] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name || !amount) return;
    onSave({ id: crypto.randomUUID(), type: 'asset', name, amount: parseFloat(amount), date: currentMonth, category, monthly_cashflow: parseFloat(cashflow) || 0 });
    setName(''); setAmount(''); setCashflow('');
  }

  return (
    <form onSubmit={submit}>
      <Field label="Asset name" value={name} onChange={setName} placeholder="e.g. Bitcoin" />
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label style={labelStyle}>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
          <option value="real_estate">Real Estate</option>
          <option value="paper">Paper Assets</option>
          <option value="business">Business</option>
          <option value="cash">Cash</option>
          <option value="other">Other</option>
        </select>
      </div>
      <Field label="Current value (£)" type="number" value={amount} onChange={setAmount} placeholder="0" />
      <Field label="Monthly cashflow (£, 0 if none)" type="number" value={cashflow} onChange={setCashflow} placeholder="0" />
      <SaveButton />
    </form>
  );
}

function LiabilityForm({ currentMonth, onSave }: { currentMonth: string; onSave: (e: QuickAddEntry) => void }) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('other');
  const [amount, setAmount] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name || !amount) return;
    onSave({ id: crypto.randomUUID(), type: 'liability', name, amount: parseFloat(amount), date: currentMonth, category });
    setName(''); setAmount('');
  }

  return (
    <form onSubmit={submit}>
      <Field label="Liability name" value={name} onChange={setName} placeholder="e.g. Personal loan" />
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label style={labelStyle}>Category</label>
        <select value={category} onChange={(e) => setCategory(e.target.value)} style={inputStyle}>
          <option value="mortgage">Mortgage</option>
          <option value="loan">Loan</option>
          <option value="credit_card">Credit Card</option>
          <option value="other">Other</option>
        </select>
      </div>
      <Field label="Outstanding balance (£)" type="number" value={amount} onChange={setAmount} placeholder="0" />
      <SaveButton />
    </form>
  );
}

function NoteForm({ currentMonth, onSave }: { currentMonth: string; onSave: (a: MonthAnnotation) => void }) {
  const [note, setNote] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!note.trim()) return;
    onSave({ month: currentMonth, note: note.trim() });
    setNote('');
  }

  return (
    <form onSubmit={submit}>
      <div style={{ marginBottom: 'var(--spacing-md)' }}>
        <label style={labelStyle}>Month note for {currentMonth}</label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Sold investment property this month"
          rows={4}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>
      <SaveButton />
    </form>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: 'var(--color-text-secondary)',
  marginBottom: 4,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: '0.9rem',
  outline: 'none',
};

function Field({ label, value, onChange, placeholder, type = 'text' }: { label: string; value: string; onChange: (v: string) => void; placeholder: string; type?: string }) {
  return (
    <div style={{ marginBottom: 'var(--spacing-md)' }}>
      <label style={labelStyle}>{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
    </div>
  );
}

function SaveButton() {
  return (
    <button type="submit" style={{ width: '100%', padding: '10px', background: 'var(--color-accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem' }}>
      Save
    </button>
  );
}
